package network

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch

/**
 * Verifica la versión de la app contra la mínima requerida en Firebase.
 *
 * Estados:
 * - requiereActualizacion = false → todo normal
 * - requiereActualizacion = true → modo lectura, banner visible, escrituras bloqueadas
 */
class VersionViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val _requiereActualizacion = MutableStateFlow(false)
    val requiereActualizacion: StateFlow<Boolean> = _requiereActualizacion

    private val _minVersionRemota = MutableStateFlow(0)
    val minVersionRemota: StateFlow<Int> = _minVersionRemota

    private val _mensajeActualizacion = MutableStateFlow("")
    val mensajeActualizacion: StateFlow<String> = _mensajeActualizacion

    private val _urlActualizacion = MutableStateFlow("")
    val urlActualizacion: StateFlow<String> = _urlActualizacion

    /** true cuando la verificación inicial terminó */
    private val _verificado = MutableStateFlow(false)
    val verificado: StateFlow<Boolean> = _verificado

    /**
     * Verificar versión contra Firebase. Llamar una vez al inicio de la app.
     * Lee config/minVersion, config/updateMessage, config/updateUrl de Firebase RTDB.
     */
    fun verificarVersion() {
        scope.launch {
            try {
                val db = FirebaseClient.database
                val configSnap = db.reference("config").valueEvents.first()

                if (configSnap.exists) {
                    val minVersion = try {
                        configSnap.child("minVersion").value<Int>()
                    } catch (_: Exception) {
                        try {
                            configSnap.child("minVersion").value<Long>().toInt()
                        } catch (_: Exception) { 0 }
                    }

                    val mensaje = try {
                        configSnap.child("updateMessage").value<String>()
                    } catch (_: Exception) { "" }

                    val url = try {
                        configSnap.child("updateUrl").value<String>()
                    } catch (_: Exception) { "" }

                    _minVersionRemota.value = minVersion
                    _mensajeActualizacion.value = mensaje
                    _urlActualizacion.value = url

                    // La WEB siempre sirve la última versión desplegada, así que NUNCA está
                    // "desactualizada". El bloqueo por versión es solo para APKs nativas viejas.
                    val esWeb = org.example.abigail.getPlatform().name.contains("Web", ignoreCase = true) ||
                        org.example.abigail.getPlatform().name.contains("Kotlin/JS", ignoreCase = true)
                    _requiereActualizacion.value = !esWeb && AppConfig.VERSION_CODE < minVersion
                    VersionGate.bloqueado = _requiereActualizacion.value
                }
            } catch (_: Exception) {
                // Si falla la verificación (offline, etc.), permitir uso normal
                _requiereActualizacion.value = false
                VersionGate.bloqueado = false
            } finally {
                _verificado.value = true
            }
        }
    }

    /**
     * Retorna true si la app puede hacer escrituras (versión OK).
     * Usar antes de cualquier operación de escritura.
     */
    fun puedeEscribir(): Boolean = !_requiereActualizacion.value

    fun onCleared() {
        scope.cancel()
    }
}
